import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Icon } from '@/components/icons'

type ToastKind = 'info' | 'success' | 'error'

/**
 * 提示里的一颗可选动作按钮（如「撤销」）。
 *
 * 存在的理由只有一个：用户 2026-09-30 的话「以后也不要因为清空而失去」——
 * 「清理」是可逆的，但**可逆这件事本身得看得见**。一句「已清理 3 条」只说明
 * 发生了什么，旁边那颗「撤销」才能让人**当场**把东西拿回来，而不用先去
 * 任务中心找「已清理」。
 */
type ToastAction = { label: string; onClick: () => void }
type Toast = { id: number; kind: ToastKind; text: string; action?: ToastAction }

type ToastApi = {
  toast: (text: string, kind?: ToastKind, action?: ToastAction) => void
  success: (text: string) => void
  error: (text: string) => void
}

const Ctx = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])
  const seq = useRef(0)

  const toast = useCallback((text: string, kind: ToastKind = 'info', action?: ToastAction) => {
    const id = ++seq.current
    setItems((list) => [...list, { id, kind, text, action }])
    /*
     * 带动作的提示**活得久一点**（8s vs 3.6s）。
     *
     * 3.6 秒不够人读完一句话再决定要不要点「撤销」—— 还没抬手就消失了，
     * 等于没这个功能，而用户只会记得「它好像说过能撤销，但按钮一闪就没了」。
     */
    window.setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), action ? 8000 : 3600)
  }, [])

  const value = useMemo<ToastApi>(
    () => ({
      toast,
      success: (t) => toast(t, 'success'),
      error: (t) => toast(t, 'error'),
    }),
    [toast],
  )

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-wrap">
        {items.map((t) => {
          // 先取出来：闭包里 TS 不会保留 `t.action` 的非空收窄
          const act = t.action
          return (
            <div key={t.id} className={`toast${t.kind === 'info' ? '' : ` toast--${t.kind}`}`}>
              <Icon name={t.kind === 'error' ? 'alert' : t.kind === 'success' ? 'check' : 'info'} size={14} />
              <span>{t.text}</span>
              {act ? (
                <button
                  className="toast__action"
                  type="button"
                  onClick={() => {
                    // 点完立刻收起：动作是一次性的，留着会让人以为还能再点一次
                    setItems((list) => list.filter((x) => x.id !== t.id))
                    act.onClick()
                  }}
                >
                  {act.label}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}

export function useToast() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
